import React, { useRef, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  CertificateAlign,
  CertificateElement,
  CertificatePageSettings,
  CertificateTemplate,
} from "../certificatePrint/certificateTypes";
import CertificateTemplateSelector from "../certificatePrint/CertificateTemplateSelector";
import ImageCropModal from "../../../myaccount/clinic/ImageCropModal";
import { InvoicePrintDefault, PAPER_SIZE_OPTIONS } from "./newBillTypes";
import { isPrintTemplate2Enabled } from "../../../../utils/clinicFeatures";

type NewBillSettingsPanelProps = {
  templates: CertificateTemplate[];
  activeId: string;
  defaultId: string;
  page: CertificatePageSettings;
  elements: CertificateElement[];
  selected: CertificateElement | null;
  invoicePrintDefault: InvoicePrintDefault;
  onInvoicePrintDefaultChange: (value: InvoicePrintDefault) => void;
  onSelectTemplate: (id: string) => void;
  onRename: (name: string) => void;
  onSaveAsNew: () => void;
  onDuplicate: () => void;
  onReset: () => void;
  onSetDefault: () => void;
  onPageChange: (patch: Partial<CertificatePageSettings>) => void;
  onElementChange: (id: string, patch: Partial<CertificateElement>) => void;
  onToggleField: (ids: string[], visible: boolean) => void;
  onSelectElement?: (id: string | null) => void;
  onSave: () => void;
};

const FONT_FAMILIES = [
  "Helvetica, Arial, sans-serif",
  "Georgia, 'Times New Roman', serif",
  "'Times New Roman', Times, serif",
  "Inter, sans-serif",
  "Poppins, sans-serif",
];

function num(value: string, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function NewBillSettingsPanel({
  templates,
  activeId,
  defaultId,
  page,
  elements,
  selected,
  invoicePrintDefault,
  onInvoicePrintDefaultChange,
  onSelectTemplate,
  onRename,
  onSaveAsNew,
  onDuplicate,
  onReset,
  onSetDefault,
  onPageChange,
  onElementChange,
  onToggleField,
  onSelectElement,
  onSave,
}: NewBillSettingsPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const letterheadRef = useRef<HTMLInputElement>(null);
  const allIds = elements.map((item) => item.id);
  const printTemplate2Enabled = isPrintTemplate2Enabled();
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  return (
    <div className="certificate-settings-body">
      <div className="certificate-section">
        <h3>Default invoice print</h3>
        <p className="certificate-field-hint">
          Choose which layout Invoice and Patient payment print use by default.
          If New Bill Print is selected, the header Print switcher is hidden and
          that layout stays locked until you change this default.
        </p>
        <div className="certificate-field">
          <label>Default engine</label>
          <select
            value={invoicePrintDefault}
            onChange={(event) =>
              onInvoicePrintDefaultChange(event.target.value as InvoicePrintDefault)
            }
          >
            <option value="bill">Bill (classic)</option>
            <option value="newBillPrint">New Bill Print</option>
            {printTemplate2Enabled ? (
              <option value="printTemplate_2">Print Template 2</option>
            ) : null}
          </select>
        </div>
      </div>

      <CertificateTemplateSelector
        templates={templates}
        activeId={activeId}
        defaultId={defaultId}
        onSelect={onSelectTemplate}
        onRename={onRename}
        onSaveAsNew={onSaveAsNew}
        onDuplicate={onDuplicate}
        onReset={onReset}
        onSetDefault={onSetDefault}
      />

      <div className="certificate-section">
        <h3>Show / hide fields</h3>
        <div className="certificate-template-actions" style={{ marginBottom: 10 }}>
          <button type="button" className="certificate-btn" onClick={() => onToggleField(allIds, true)}>
            Show all
          </button>
          <button
            type="button"
            className="certificate-btn"
            onClick={() => onToggleField(allIds, false)}
          >
            Hide all
          </button>
        </div>
        <div className="certificate-field-list">
          {elements.map((item) => (
            <div
              key={item.id}
              className={`certificate-field-row${selected?.id === item.id ? " is-selected" : ""}`}
              onClick={() => onSelectElement?.(item.id)}
            >
              <span>{item.label}</span>
              <button
                type="button"
                className="certificate-icon-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleField([item.id], !item.visible);
                }}
              >
                {item.visible ? (
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                ) : (
                  <VisibilityOffIcon sx={{ fontSize: 18 }} />
                )}
                <span>{item.visible ? "Hide" : "Show"}</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="certificate-section">
        <h3>Page</h3>
        <div className="certificate-row">
          <div className="certificate-field">
            <label>Size</label>
            <select
              value={page.size}
              onChange={(event) =>
                onPageChange({
                  size: event.target.value as CertificatePageSettings["size"],
                })
              }
            >
              {PAPER_SIZE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.hint})
                </option>
              ))}
            </select>
          </div>
          <div className="certificate-field">
            <label>Orientation</label>
            <select
              value={page.orientation}
              onChange={(event) =>
                onPageChange({
                  orientation: event.target.value as CertificatePageSettings["orientation"],
                })
              }
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>
        </div>
        <div className="certificate-field">
          <label>Margins (mm)</label>
          <input
            type="number"
            min={6}
            max={40}
            value={page.marginMm}
            onChange={(event) => onPageChange({ marginMm: num(event.target.value, page.marginMm) })}
          />
        </div>
        <label className="certificate-check">
          <input
            type="checkbox"
            checked={page.showSafeArea}
            onChange={(event) => onPageChange({ showSafeArea: event.target.checked })}
          />
          Show print-safe area
        </label>
      </div>

      <div className="certificate-section">
        <h3>Letterhead</h3>
        <p className="certificate-field-hint">
          Upload an image and crop it. Showing letterhead hides the classic logo+header stack if you prefer.
        </p>
        <div className="certificate-field">
          <label>Upload letterhead</label>
          <input
            ref={letterheadRef}
            type="file"
            accept="image/*"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                if (typeof reader.result === "string") {
                  setCropSrc(reader.result);
                }
              };
              reader.readAsDataURL(file);
              event.target.value = "";
            }}
          />
        </div>
        {page.letterheadSrc ? (
          <div className="certificate-field">
            <img
              src={page.letterheadSrc}
              alt="Letterhead preview"
              style={{ maxWidth: "100%", maxHeight: 80, objectFit: "contain", borderRadius: 6 }}
            />
            <div className="certificate-template-actions" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="certificate-btn"
                onClick={() => {
                  onPageChange({ letterheadSrc: "" });
                  onElementChange("letterhead", { visible: false });
                }}
              >
                Clear letterhead
              </button>
              <button
                type="button"
                className="certificate-btn certificate-btn-primary"
                onClick={() => onElementChange("letterhead", { visible: true })}
              >
                Show on bill
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="certificate-section">
        <h3>Typography</h3>
        <p className="certificate-field-hint">
          Font family and size apply to every text and table field on the bill.
        </p>
        <div className="certificate-field">
          <label>Font family</label>
          <select
            value={page.fontFamily}
            onChange={(event) => onPageChange({ fontFamily: event.target.value })}
          >
            {FONT_FAMILIES.map((font) => (
              <option key={font} value={font}>
                {font.split(",")[0].replace(/'/g, "")}
              </option>
            ))}
          </select>
        </div>
        <div className="certificate-row">
          <div className="certificate-field">
            <label>Font size (pt)</label>
            <input
              type="number"
              min={8}
              max={18}
              value={page.fontSize}
              onChange={(event) =>
                onPageChange({ fontSize: num(event.target.value, page.fontSize) })
              }
            />
          </div>
          <div className="certificate-field">
            <label>Align</label>
            <select
              value={page.textAlign}
              onChange={(event) =>
                onPageChange({ textAlign: event.target.value as CertificateAlign })
              }
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </div>
      </div>

      <div className="certificate-section">
        <h3>Clinic logo</h3>
        <div className="certificate-field">
          <label>Logo</label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                if (typeof reader.result === "string") {
                  onPageChange({ logoSrc: reader.result });
                }
              };
              reader.readAsDataURL(file);
            }}
          />
        </div>
        <div className="certificate-field">
          <label>Logo size (mm)</label>
          <input
            type="number"
            min={12}
            max={60}
            value={page.logoSizeMm}
            onChange={(event) => {
              const logoSizeMm = num(event.target.value, page.logoSizeMm);
              onPageChange({ logoSizeMm });
              onElementChange("logo", { width: logoSizeMm, height: logoSizeMm * 0.65 });
            }}
          />
        </div>
      </div>

      {selected ? (
        <div className="certificate-section">
          <h3>Selected field</h3>
          <div className="certificate-field">
            <label>{selected.label}</label>
            {selected.type === "text" ? (
              <textarea
                rows={3}
                value={selected.content}
                onChange={(event) => onElementChange(selected.id, { content: event.target.value })}
              />
            ) : (
              <p className="certificate-field-hint">
                {selected.type === "logo"
                  ? "Logo / letterhead position can be dragged on the canvas."
                  : "Table data is filled from invoice treatments and amounts. Resize on canvas or set height below."}
              </p>
            )}
          </div>
          {selected.type === "table" ? (
            <div className="certificate-row">
              <div className="certificate-field">
                <label>Width (mm)</label>
                <input
                  type="number"
                  min={20}
                  max={280}
                  step={0.5}
                  value={Math.round(selected.width * 10) / 10}
                  onChange={(event) =>
                    onElementChange(selected.id, {
                      width: num(event.target.value, selected.width),
                    })
                  }
                />
              </div>
              <div className="certificate-field">
                <label>Height (mm)</label>
                <input
                  type="number"
                  min={10}
                  max={280}
                  step={0.5}
                  value={Math.round(selected.height * 10) / 10}
                  onChange={(event) =>
                    onElementChange(selected.id, {
                      height: num(event.target.value, selected.height),
                    })
                  }
                />
              </div>
            </div>
          ) : null}
          {selected.fieldKey === "amountTable" || selected.id === "billDetails" ? (
            <div className="certificate-field">
              <label>Cell padding (mm)</label>
              <input
                type="number"
                min={0}
                max={8}
                step={0.1}
                value={selected.paddingMm ?? 1.2}
                onChange={(event) =>
                  onElementChange(selected.id, {
                    paddingMm: num(event.target.value, selected.paddingMm ?? 1.2),
                    backgroundColor: undefined,
                  })
                }
              />
              <p className="certificate-field-hint">
                Amount rows have no background fill. Adjust padding and table height as needed.
              </p>
            </div>
          ) : null}
          <div className="certificate-row">
            <div className="certificate-field">
              <label>Font size (pt)</label>
              <input
                type="number"
                min={7}
                max={28}
                value={selected.fontSize}
                onChange={(event) =>
                  onElementChange(selected.id, {
                    fontSize: num(event.target.value, selected.fontSize),
                  })
                }
              />
            </div>
            <div className="certificate-field">
              <label>Align</label>
              <select
                value={selected.align}
                onChange={(event) =>
                  onElementChange(selected.id, {
                    align: event.target.value as CertificateAlign,
                  })
                }
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>
          <div className="certificate-field">
            <label>Font family</label>
            <select
              value={selected.fontFamily || page.fontFamily}
              onChange={(event) =>
                onElementChange(selected.id, {
                  fontFamily: event.target.value,
                })
              }
            >
              {FONT_FAMILIES.map((font) => (
                <option key={font} value={font}>
                  {font.split(",")[0].replace(/'/g, "")}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : null}

      <div className="certificate-template-actions" style={{ marginTop: 12 }}>
        <button type="button" className="certificate-btn certificate-btn-primary" onClick={onSave}>
          Save template
        </button>
      </div>

      {cropSrc ? (
        <ImageCropModal
          src={cropSrc}
          title="Crop letterhead"
          freeCrop
          onCancel={() => setCropSrc(null)}
          onConfirm={(dataUrl) => {
            onPageChange({ letterheadSrc: dataUrl });
            onElementChange("letterhead", { visible: true });
            setCropSrc(null);
          }}
        />
      ) : null}
    </div>
  );
}

export default NewBillSettingsPanel;
